import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable, BandMeter } from '../_fitnessHelpers';

export default function BodyFatCalc() {
  const [tab, setTab] = useState(0);
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState(25);
  // US
  const [wtLb, setWtLb] = useState(152);
  const [hFt, setHFt] = useState(5);
  const [hIn, setHIn] = useState(10.5);
  const [nFt, setNFt] = useState(1);
  const [nIn, setNIn] = useState(7.5);
  const [wFt, setWFt] = useState(3);
  const [wIn, setWIn] = useState(1.5);
  const [hipFt, setHipFt] = useState(3);
  const [hipIn, setHipIn] = useState(6);
  // Metric
  const [wtKg, setWtKg] = useState(69);
  const [hCm, setHCm] = useState(179);
  const [nCm, setNCm] = useState(48);
  const [wCm, setWCm] = useState(94);
  const [hipCm, setHipCm] = useState(91);

  const clear = () => {
    setGender('male'); setAge(25);
    setWtLb(152); setHFt(5); setHIn(10.5); setNFt(1); setNIn(7.5); setWFt(3); setWIn(1.5); setHipFt(3); setHipIn(6);
    setWtKg(69); setHCm(179); setNCm(48); setWCm(94); setHipCm(91);
  };

  const c = useMemo(() => {
    // convert everything to cm & kg for calc
    const inToCm = 2.54;
    const lbToKg = 0.453592;
    let heightCm, neckCm, waistCm, hipCmVal, weightKg;
    if (tab === 0) {
      heightCm = ((+hFt || 0) * 12 + (+hIn || 0)) * inToCm;
      neckCm = ((+nFt || 0) * 12 + (+nIn || 0)) * inToCm;
      waistCm = ((+wFt || 0) * 12 + (+wIn || 0)) * inToCm;
      hipCmVal = ((+hipFt || 0) * 12 + (+hipIn || 0)) * inToCm;
      weightKg = (+wtLb || 0) * lbToKg;
    } else {
      heightCm = +hCm || 0; neckCm = +nCm || 0; waistCm = +wCm || 0; hipCmVal = +hipCm || 0;
      weightKg = +wtKg || 0;
    }
    // US Navy metric formula
    let bfNavy;
    if (gender === 'male') {
      bfNavy = 495/(1.0324 - 0.19077*Math.log10(Math.max(1, waistCm - neckCm)) + 0.15456*Math.log10(Math.max(1,heightCm))) - 450;
    } else {
      bfNavy = 495/(1.29579 - 0.35004*Math.log10(Math.max(1, waistCm + hipCmVal - neckCm)) + 0.22100*Math.log10(Math.max(1,heightCm))) - 450;
    }
    bfNavy = Math.max(0, bfNavy);
    // BMI method: BF% ≈ 1.20 × BMI + 0.23 × Age − 10.8 × sex − 5.4 (sex = 1 male, 0 female)
    const bmi = heightCm > 0 ? weightKg / Math.pow(heightCm/100, 2) : 0;
    const bfBmi = Math.max(0, 1.2*bmi + 0.23*age - 10.8*(gender === 'male' ? 1 : 0) - 5.4);
    const fatMass = weightKg * bfNavy / 100;
    const leanMass = weightKg - fatMass;
    // Ideal BF Jackson & Pollock (approx by age)
    const ideal = gender === 'male' ? 8 + Math.max(0, (age - 20)) * 0.2 : 20 + Math.max(0, (age - 20)) * 0.2;
    const toLose = Math.max(0, fatMass - (weightKg * ideal / 100));

    let cat;
    if (gender === 'male') {
      if (bfNavy < 6) cat = 'Essential';
      else if (bfNavy < 14) cat = 'Athletes';
      else if (bfNavy < 18) cat = 'Fitness';
      else if (bfNavy < 25) cat = 'Average';
      else cat = 'Obese';
    } else {
      if (bfNavy < 14) cat = 'Essential';
      else if (bfNavy < 21) cat = 'Athletes';
      else if (bfNavy < 25) cat = 'Fitness';
      else if (bfNavy < 32) cat = 'Average';
      else cat = 'Obese';
    }
    const lbFromKg = k => tab === 0 ? (k / lbToKg).toFixed(1) + ' lbs' : k.toFixed(1) + ' kg';
    return { bfNavy, bfBmi, fatMass, leanMass, ideal, toLose, cat, lbFromKg };
  }, [tab, gender, age, wtLb, hFt, hIn, nFt, nIn, wFt, wIn, hipFt, hipIn, wtKg, hCm, nCm, wCm, hipCm]);

  const bands = gender === 'male' ? [
    { start: 0, end: 6, color: '#a63946', rangeLabel: '2%', label: 'Essential' },
    { start: 6, end: 14, color: '#f6d33c', rangeLabel: '6%', label: 'Athletes' },
    { start: 14, end: 18, color: '#3fa34d', rangeLabel: '14%', label: 'Fitness' },
    { start: 18, end: 25, color: '#f6d33c', rangeLabel: '18%', label: 'Average' },
    { start: 25, end: 40, color: '#a63946', rangeLabel: '25%', label: 'Obese' },
  ] : [
    { start: 0, end: 14, color: '#a63946', rangeLabel: '10%', label: 'Essential' },
    { start: 14, end: 21, color: '#f6d33c', rangeLabel: '14%', label: 'Athletes' },
    { start: 21, end: 25, color: '#3fa34d', rangeLabel: '21%', label: 'Fitness' },
    { start: 25, end: 32, color: '#f6d33c', rangeLabel: '25%', label: 'Average' },
    { start: 32, end: 45, color: '#a63946', rangeLabel: '32%', label: 'Obese' },
  ];

  const showHip = gender === 'female';

  return (
    <CalcShell look="cnet" title="Body Fat Calculator" subtitle='U.S. Navy Method — total body fat from measurements. Use "Metric Units" for SI.'>
      <CalcNetPanel
        slug="bodyfat"
        activeTab={tab}
        onTabChange={setTab}
        onClear={clear}
        right={
          <div>
            <div className="text-xl md:text-2xl font-bold text-[#2f9c3f]">Body Fat: {c.bfNavy.toFixed(1)}%</div>
            <BandMeter bands={bands} value={c.bfNavy} formatValue={v => v.toFixed(1) + '%'} />
            <ResultTable
              testid="bodyfat-result-table"
              rows={[
                ['Body Fat (U.S. Navy Method)', `${c.bfNavy.toFixed(1)}%`],
                ['Body Fat Category', c.cat],
                ['Body Fat Mass', c.lbFromKg(c.fatMass)],
                ['Lean Body Mass', c.lbFromKg(c.leanMass)],
                ['Ideal Body Fat for Given Age (Jackson & Pollock)', `${c.ideal.toFixed(1)}%`],
                ['Body Fat to Lose to Reach Ideal', c.lbFromKg(c.toLose)],
                ['Body Fat (BMI method)', `${c.bfBmi.toFixed(1)}%`],
              ]}
            />
          </div>
        }
      >
        <Row label="Gender"><RadioGroup name="bf-gender" value={gender} onChange={setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="bf-age" /></Row>
        {tab === 0 ? <>
          <Row label="Weight"><UnitInput value={wtLb} onChange={setWtLb} unit="pounds" testid="bf-wt" /></Row>
          <Row label="Height"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={hFt} inches={hIn} onFt={setHFt} onIn={setHIn} testidPrefix="bf-h" /></div></Row>
          <Row label="Neck"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={nFt} inches={nIn} onFt={setNFt} onIn={setNIn} testidPrefix="bf-n" /></div></Row>
          <Row label="Waist"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={wFt} inches={wIn} onFt={setWFt} onIn={setWIn} testidPrefix="bf-w" /></div></Row>
          {showHip && <Row label="Hip"><div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={hipFt} inches={hipIn} onFt={setHipFt} onIn={setHipIn} testidPrefix="bf-hip" /></div></Row>}
        </> : <>
          <Row label="Weight"><UnitInput value={wtKg} onChange={setWtKg} unit="kg" testid="bf-wt-kg" /></Row>
          <Row label="Height"><UnitInput value={hCm} onChange={setHCm} unit="cm" testid="bf-h-cm" /></Row>
          <Row label="Neck"><UnitInput value={nCm} onChange={setNCm} unit="cm" testid="bf-n-cm" /></Row>
          <Row label="Waist"><UnitInput value={wCm} onChange={setWCm} unit="cm" testid="bf-w-cm" /></Row>
          {showHip && <Row label="Hip"><UnitInput value={hipCm} onChange={setHipCm} unit="cm" testid="bf-hip-cm" /></Row>}
        </>}
      </CalcNetPanel>

      <StepsPanel steps={[
        `U.S. Navy method uses circumference measurements (neck, waist${showHip?', hip':''}) plus height.`,
        `Body Fat % = ${c.bfNavy.toFixed(1)}% → Category: <b>${c.cat}</b>`,
        `Body fat mass ≈ ${c.lbFromKg(c.fatMass)} · Lean body mass ≈ ${c.lbFromKg(c.leanMass)}`,
      ]} />
    </CalcShell>
  );
}
